import {
    getPrimaryAnalysis,
} from "@mrflark/spl_sdk";
import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Badge,
    Modal,
} from "@mrflark/ui";
import {
    Check,
    Crown,
    X,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";
import {
    useState,
} from "preact/compat";

type AiAnalysisModalProps = {
    isOpen: boolean;
    onClose: () => void;
    item: MediaItem | null;
    onAcceptSuggestion?: (tag: string) => void;
    onRejectSuggestion?: (tag: string) => void;
};

const SectionLabel = ({
    children,
}: { children: string; }): ReactElement => {
    return (
        <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
            {children}
        </span>
    );
};

const TagList = ({
    label,
    tags,
}: { label: string; tags: string[]; }): ReactElement => {
    return (
        <div className="space-y-1.5">
            <SectionLabel>{label}</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => <Badge key={tag}>{tag}</Badge>)}
            </div>
        </div>
    );
};

const SuggestedTagList = ({
    tags,
    onAccept,
    onReject,
}: {
    tags: string[];
    onAccept?: (tag: string) => void;
    onReject?: (tag: string) => void;
}): ReactElement => {
    return (
        <div className="space-y-1.5">
            <SectionLabel>Suggested Tags</SectionLabel>
            <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center gap-1 rounded-md bg-accent/10 border border-accent/30 px-2 py-0.5 text-xs text-text-primary"
                    >
                        {tag}
                        {onAccept && (
                            <button
                                type="button"
                                onClick={() => onAccept(tag)}
                                className="p-0.5 rounded hover:bg-green-500/20 text-green-400 transition-colors"
                                title="Accept suggestion"
                            >
                                <Check size={12} />
                            </button>
                        )}
                        {onReject && (
                            <button
                                type="button"
                                onClick={() => onReject(tag)}
                                className="p-0.5 rounded hover:bg-red-500/20 text-red-400 transition-colors"
                                title="Dismiss suggestion"
                            >
                                <X size={12} />
                            </button>
                        )}
                    </span>
                ))}
            </div>
        </div>
    );
};

const ModelTabBar = ({
    models,
    activeModel,
    primaryModel,
    onSelect,
}: {
    models: string[];
    activeModel: string;
    primaryModel: string | null;
    onSelect: (model: string) => void;
}): ReactElement => {
    const shortName = (model: string): string => {
        const parts = model.split("/");
        return parts[parts.length - 1];
    };

    return (
        <div className="flex gap-1 border-b border-border pb-2 mb-3">
            {models.map((model) => (
                <button
                    type="button"
                    key={model}
                    onClick={() => onSelect(model)}
                    className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-t transition-colors ${
                        model === activeModel
                            ? "bg-accent/20 text-accent border-b-2 border-accent font-medium"
                            : "text-text-muted hover:text-text-secondary hover:bg-surface-hover"
                    }`}
                >
                    {model === primaryModel && (
                        <Crown size={12} className="text-yellow-400" />
                    )}
                    {shortName(model)}
                </button>
            ))}
        </div>
    );
};

const AnalysisContent = ({
    description,
    suggestedTags,
    objects,
    searchKeywords,
    confidence,
    modelName,
    analyzedAt,
    onAcceptSuggestion,
    onRejectSuggestion,
}: {
    description?: string;
    suggestedTags?: string[];
    objects?: string[];
    searchKeywords?: string[];
    confidence?: number;
    modelName?: string;
    analyzedAt?: string;
    onAcceptSuggestion?: (tag: string) => void;
    onRejectSuggestion?: (tag: string) => void;
}): ReactElement => {
    return (
        <div className="space-y-4">
            {description && (
                <p className="text-text-primary text-sm leading-relaxed">
                    {description}
                </p>
            )}

            {confidence !== undefined && (
                <div className="flex flex-wrap gap-4">
                    <div className="space-y-1">
                        <SectionLabel>Confidence</SectionLabel>
                        <div>
                            <Badge>{(confidence * 100).toFixed(0)}%</Badge>
                        </div>
                    </div>
                </div>
            )}

            {suggestedTags && suggestedTags.length > 0 && (
                <SuggestedTagList
                    tags={suggestedTags}
                    onAccept={onAcceptSuggestion}
                    onReject={onRejectSuggestion}
                />
            )}

            {objects && objects.length > 0 && (
                <TagList label="Objects" tags={objects} />
            )}

            {searchKeywords && searchKeywords.length > 0 && (
                <TagList label="Search Keywords" tags={searchKeywords} />
            )}

            {(modelName || analyzedAt) && (
                <div className="flex flex-wrap gap-3 text-xs text-text-muted pt-2 border-t border-border">
                    {modelName && <span>Model: {modelName}</span>}
                    {analyzedAt && (
                        <span>
                            Analyzed:{" "}
                            {new Date(analyzedAt).toLocaleDateString()}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export const AiAnalysisModal = ({
    isOpen,
    onClose,
    item,
    onAcceptSuggestion,
    onRejectSuggestion,
}: AiAnalysisModalProps): ReactElement => {
    const [selectedModel, setSelectedModel] = useState<string | null>(null);

    const analyses = item?.ai_analyses;
    const modelKeys = analyses ? Object.keys(analyses) : [];
    const hasMultipleModels = modelKeys.length > 1;
    const primaryResult = getPrimaryAnalysis(analyses);
    const primaryModel = primaryResult?.modelName ?? null;
    const hasAiData = modelKeys.length > 0;

    const activeModel = selectedModel && modelKeys.includes(selectedModel)
        ? selectedModel
        : primaryModel ?? modelKeys[0] ?? null;

    const activeEntry = activeModel ? analyses?.[activeModel] : undefined;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="AI Analysis" size="md">
            {!hasAiData
                ? (
                    <p className="text-text-secondary">
                        No AI analysis available for this item.
                    </p>
                )
                : hasMultipleModels && activeModel && activeEntry
                ? (
                    <div>
                        <ModelTabBar
                            models={modelKeys}
                            activeModel={activeModel}
                            primaryModel={primaryModel}
                            onSelect={setSelectedModel}
                        />
                        <AnalysisContent
                            description={activeEntry.description}
                            suggestedTags={activeEntry.suggested_tags}
                            objects={activeEntry.objects}
                            confidence={activeEntry.confidence}
                            modelName={activeModel}
                            analyzedAt={activeEntry.analyzed_at}
                            onAcceptSuggestion={onAcceptSuggestion}
                            onRejectSuggestion={onRejectSuggestion}
                        />
                    </div>
                )
                : activeModel && activeEntry
                ? (
                    <AnalysisContent
                        description={activeEntry.description}
                        suggestedTags={activeEntry.suggested_tags}
                        objects={activeEntry.objects}
                        searchKeywords={item?.search_keywords}
                        confidence={activeEntry.confidence}
                        modelName={activeModel}
                        analyzedAt={activeEntry.analyzed_at}
                        onAcceptSuggestion={onAcceptSuggestion}
                        onRejectSuggestion={onRejectSuggestion}
                    />
                )
                : (
                    <p className="text-text-secondary">
                        No AI analysis available for this item.
                    </p>
                )}

            {/* watermark fields still on API response but removed from TypeSpec type */}
            {(item as MediaItem & {
                has_watermark?: boolean;
                watermark_description?: string;
            })?.has_watermark && (
                <div className="mt-4 pt-4 border-t border-border">
                    <SectionLabel>Watermark Detection</SectionLabel>
                    <div className="mt-1 flex items-center gap-2">
                        <Badge>Watermark Detected</Badge>
                        {(item as MediaItem & {
                            watermark_description?: string;
                        }).watermark_description && (
                            <span className="text-xs text-text-secondary">
                                {(item as MediaItem & {
                                    watermark_description?: string;
                                }).watermark_description}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};
